import orderApi from '@/api/order';
import posApi from '@/api/pos';
import { TakeAwayProps } from '@/interfaces/models/order';
import { PosCartItem, PosCartPayload, PosSyncPaymentEmit } from '@/interfaces/models/posSync';
import Client from '@/models/client';
import Ingredient from '@/models/ingredient';
import OrderProduct from '@/models/orderProduct';
import OrderProductIngredient from '@/models/orderProductIngredient';
import OrderProductInterface from '@/models/orderProductInterface';
import OrderStaff from '@/models/orderStaff';
import Pos from '@/models/pos';
import PosOrderProduct, { GroupIngredientsProps } from '@/models/posOrderProduct';
import Product from '@/models/product';
import TakeAway from '@/models/takeAway';
import { RootState } from '@/store';
import { asyncRecursive } from '@/utils';
import numberUtils from '@/utils/number.utils';
import { buildSyncPaymentEmit } from '@/utils/posSync.utils';
import assetsUtils from '@/utils/url.utils';
import Cookies from 'js-cookie';
import camelCase from 'lodash.camelcase';
import isEqual from 'lodash.isequal';
import { ActionContext, ActionTree, GetterTree, MutationTree } from 'vuex';

const requireModule = require.context('./pos', false, /\.ts$/);
const modules: any = {};

requireModule.keys().forEach((fileName: any) => {
  const moduleName = camelCase(
    fileName.replace(/(\.\/|\.ts)/g, ''),
  );
  const module = requireModule(fileName);

  modules[moduleName] = module.default || module;
});

type PosState = {
  sectionNotification: {
    actualOrdersCount: number;
    savedOrdersCount: number;
  },
  order: {
    products: OrderProductInterface[],
    client: Client | null,
    paymentMethod: string | null,
    comment: string,
    discount: number | null,
  },
  isActualOrdersFetching: boolean,
  isActualOrdersWasFetch: boolean,
  isReceiptsFetching: boolean,
  isReceiptsWasFetch: boolean,
  actualOrders: TakeAway[],
  receipts: any[],
  savedReceipts: TakeAway[],
  allReceiptsWasFetched: boolean,
  posState: Pos,
  isForceOff: boolean,
  selectedCategory: null | any,
  cash: number | string,
  card: number | string,
  syncRequestId: number,
};

const namespaced: boolean = true;

const getDefaultState = (): PosState => {
  return {
    sectionNotification: {
      actualOrdersCount: 0,
      savedOrdersCount: 0,
    },
    order: {
      products: [],
      client: null,
      paymentMethod: null,
      comment: '',
      discount: null,
    },
    isActualOrdersFetching: false,
    isActualOrdersWasFetch: false,
    isReceiptsFetching: false,
    isReceiptsWasFetch: false,
    actualOrders: [],
    receipts: [],
    savedReceipts: [],
    allReceiptsWasFetched: false,
    posState: new Pos(),
    isForceOff: Boolean(Cookies.getJSON('forceOff')),
    selectedCategory: null,
    cash: '',
    card: '',
    syncRequestId: 0,
  };
};

const state: PosState = getDefaultState();

const getters: GetterTree<PosState, RootState> = {
  receiptsGroupedByDay(state) {
    return state.receipts.reduce((receiptsGroupedByDay: any, receipt: TakeAway) => {
      const dateString = receipt.date.getFullHash();

      if (!receiptsGroupedByDay[dateString]) {
        receiptsGroupedByDay[dateString] = [receipt];
      } else {
        receiptsGroupedByDay[dateString].push(receipt);
      }

      return receiptsGroupedByDay;
    }, {});
  },
  totalCost(state) {
    const totalCost = state.order.products.reduce((sum: number, orderProduct: OrderProductInterface) => {
      sum += orderProduct.cost * orderProduct.count;

      return sum;
    }, 0);

    return Math.round(totalCost * 100) / 100;
  },
  getReceiptsByFilterCallback(state, getters) {
    return (filterCallback: Function): any => {
      const ordersByDay: any = {};

      for (let day in getters.receiptsGroupedByDay) {
        if (getters.receiptsGroupedByDay.hasOwnProperty(day)) {
          const orders = getters.receiptsGroupedByDay[day].filter(filterCallback);

          if (orders.length > 0) {
            ordersByDay[day] = orders;
          }
        }
      }

      return ordersByDay;
    };
  },
  getProductFromOrder(state) {
    return (searchProduct: { id: string, ingredients: Ingredient[] }) => {
      return state.order.products.find((orderProduct: OrderProductInterface) => {
        if (orderProduct.product !== null) {
          return searchProduct.id === orderProduct.product.id
            && isEqual(orderProduct.ingredients, searchProduct.ingredients);
        }

        return false;
      });
    };
  },
  getCreateOrderRequestPayloadByCash(state) {
    return (clientCash: any, cardCash: any, posId: string | null) => ({
      type: 2,
      products: state.order.products,
      typeOfPayment: state.order.paymentMethod,
      clientCash,
      cardCash,
      client: state.order.client,
      comment: state.order.comment,
      discount: state.order.discount,
      posId,
    });
  },
  saveOrderRequestPayload(state) {
    return {
      type: 2,
      products: state.order.products,
      client: state.order.client,
      comment: state.order.comment,
    };
  },
  cartPayload(state, getters, rootState: any): PosCartPayload {
    const currency = rootState.place.selectedPlace ? rootState.place.selectedPlace.currency : 'р.';

    const products = state.order.products.reduce((flatList: PosCartItem[], orderProduct: OrderProductInterface) => {
      if (!orderProduct.product) {
        return flatList;
      }

      flatList.push({
        count: orderProduct.count,
        image: orderProduct.product.image.path
          ? assetsUtils.generateAssetsUrl(orderProduct.product.image.path).href
          : null,
        name: orderProduct.product.title,
        price: {
          amount: orderProduct.product.cost,
          currency,
        },
        weight: orderProduct.product.weight,
      });

      orderProduct.ingredients.forEach((orderProductIngredient) => {
        if (!orderProductIngredient.ingredient) {
          return;
        }

        flatList.push({
          count: orderProductIngredient.count * orderProduct.count,
          image: orderProductIngredient.ingredient.image.path
            ? assetsUtils.generateAssetsUrl(orderProductIngredient.ingredient.image.path).href
            : null,
          name: orderProductIngredient.ingredient.name,
          price: {
            amount: orderProductIngredient.cost,
            currency,
          },
          weight: `${orderProductIngredient.unitCount} ${orderProductIngredient.ingredient.unitTranslate}`,
        });
      });

      return flatList;
    }, [] as PosCartItem[]);

    const orderAmount = getters.totalCost;

    return {
      products,
      price: {
        amount: orderAmount,
        currency,
      },
    };
  },
  syncPaymentEmit(state, getters, rootState: any): PosSyncPaymentEmit | null {
    const currency = rootState.place.selectedPlace ? rootState.place.selectedPlace.currency : 'р.';
    const selectedDiscount = state.order.discount;
    const discount = state.order.products.reduce(
      function (discount: number, product: OrderProductInterface): number {
        if (!product.product) {
          return discount;
        }

        const ingredientDiscount = product.ingredients.reduce(
          (discount: number, ingredient: OrderProductIngredient): number => {
            return discount + numberUtils.correctFloatNumber(numberUtils.calculateDiscount(ingredient.cost, selectedDiscount || 0) * ingredient.count);
          },
          .0
        );

        const productDiscount = numberUtils.correctFloatNumber(numberUtils.calculateDiscount(product.productCost, selectedDiscount || 0) + ingredientDiscount);

        return discount + numberUtils.correctFloatNumber(product.count * productDiscount);
      },
      .0,
    );

    return buildSyncPaymentEmit(
      state.order.paymentMethod,
      state.card,
      numberUtils.correctFloatNumber(getters.totalCost - discount),
      currency,
      selectedDiscount === null ? null : numberUtils.correctFloatNumber(selectedDiscount * 100),
    );
  },
};

const actions: ActionTree<PosState, RootState> = {
  socketRequestPosSync({ commit }: ActionContext<PosState, RootState>) {
    commit('bumpSyncRequestId');
  },
  async fetchActualOrders({ commit, state }: ActionContext<PosState, RootState>) {
    if (!state.isActualOrdersWasFetch) {
      commit('setIsActualOrdersFetching', true);

      return posApi.fetchActualOrders()
        .then((orders: any) => {
          commit('setActualOrders', orders);
          commit('setIsActualOrdersWasFetch', true);
          commit('setIsActualOrdersFetching', false);
        });
    }
  },
  async fetchActualOrdersForTV({ commit }, placeId: string) {
    if (!state.isActualOrdersWasFetch) {
      commit('setIsActualOrdersFetching', true);

      return posApi.fetchActualOrdersForTV(placeId)
        .then((orders: any) => {
          commit('setActualOrders', orders);
          commit('setIsActualOrdersWasFetch', true);
          commit('setIsActualOrdersFetching', false);
        });
    }
  },
  async fetchReceipts({ commit, state }: ActionContext<PosState, RootState>) {
    if (!state.isReceiptsWasFetch) {
      commit('setIsReceiptsFetching', true);

      return posApi.fetchReceipts()
        .then(({ saved, done }: any) => {
          commit('setReceiptsByDay', done);
          commit('setSavedReceipts', saved);
          commit('setIsReceiptsWasFetch', true);
          commit('setIsReceiptsFetching', false);
        });
    }
  },
  async fetchNextReceipts({ commit, state }: ActionContext<PosState, RootState>) {
    if (!state.allReceiptsWasFetched) {
      return posApi.fetchNextReceipts(state.receipts.length)
        .then((receipts: any) => {
          commit('addReceipts', receipts);

          if (receipts.length < 50) {
            commit('setAllReceiptsWasFetched', true);
          }
        });
    }
  },
  async createReceipt({ commit, getters, dispatch }: ActionContext<PosState, RootState>, payload: any) {
    const receiptProps = await posApi.createReceipt(payload);
    const order = TakeAway.createFromRequest(receiptProps);

    dispatch('socketCreateReceipt', receiptProps, { root: true });

    if (order.isDone) {
      dispatch('socketWriteOffProducts', order.getWriteOffPayload(), { root: true });
    }

    return receiptProps;
  },
  async createDeliveryReceipt({ commit, getters, dispatch }: ActionContext<PosState, RootState>, payload: any) {
    const receiptProps = await posApi.createDeliveryReceipt(payload);
    const order = TakeAway.createFromRequest(receiptProps);

    dispatch('socketCreateReceipt', receiptProps, { root: true });

    if (order.isDone) {
      dispatch('socketWriteOffProducts', order.getWriteOffPayload(), { root: true });
    }

    return receiptProps;
  },
  async repeatCreateReceipt({ commit, getters, dispatch, state }: ActionContext<PosState, RootState>, payload: any) {
    const receiptProps: any = await posApi.repeatCreateReceipt(payload);
    const order = TakeAway.createFromRequest(receiptProps);

    dispatch('socketCreatePrintedOrder', receiptProps, { root: true });

    if (order.isDone) {
      dispatch('socketWriteOffProducts', order.getWriteOffPayload(), { root: true });
    }

    return receiptProps;
  },
  async createReceiptBySavedReceipt({ dispatch }: ActionContext<PosState, RootState>, payload: any) {
    const receiptProps = await posApi.createReceiptBySavedReceipt(payload);
    const order = TakeAway.createFromRequest(receiptProps);

    dispatch('socketCreateReceiptBySavedOrder', receiptProps, { root: true });

    if (order.isDone) {
      dispatch('socketWriteOffProducts', order.getWriteOffPayload(), { root: true });
    }

    return receiptProps;
  },
  async createOrderWithoutPayment({ dispatch }: ActionContext<PosState, RootState>, payload: any) {
    const receiptProps = await posApi.createOrderWithoutPayment(payload);
    const order = TakeAway.createFromRequest(receiptProps);

    dispatch('socketCreateReceipt', receiptProps, { root: true });

    if (order.isDone) {
      dispatch('socketWriteOffProducts', order.getWriteOffPayload(), { root: true });
    }

    return receiptProps;
  },
  async createFreeReceiptByOrder({ dispatch }: ActionContext<PosState, RootState>, { id, posId }: { id: string, posId: string }) {
    const receiptProps = await posApi.createFreeReceiptByOrder(id, posId);
    const order = TakeAway.createFromRequest(receiptProps);

    dispatch('socketCreateReceiptBySavedOrder', receiptProps, { root: true });

    if (order.isDone) {
      dispatch('socketWriteOffProducts', order.getWriteOffPayload(), { root: true });
    }

    return receiptProps;
  },
  async issueAndPayOrder({ dispatch }: ActionContext<PosState, RootState>, payload: any) {
    const receiptProps = await posApi.issueAndPay(payload);
    const order = TakeAway.createFromRequest(receiptProps);

    dispatch('socketIssueAndPayOrder', receiptProps, { root: true });

    if (order.isDone) {
      dispatch('socketWriteOffProducts', order.getWriteOffPayload(), { root: true });
    }

    return receiptProps;
  },
  async issueOrder({ dispatch }: ActionContext<PosState, RootState>, { id, posId }: { id: string, posId: string }) {
    posApi.issueOrder(id, posId);

    return dispatch('socketIssueOrder', { id }, { root: true });
  },
  async createSavedReceipt({ dispatch, commit, getters, state }: ActionContext<PosState, RootState>) {
    if (state.order.products.length > 0) {
      const receiptProps = await posApi.createSavedReceipt(getters.saveOrderRequestPayload);

      dispatch('socketCreateSavedReceipt', receiptProps, { root: true });

      commit('removeOrder');
    }
  },
  async setClientToOrder({ dispatch }: ActionContext<PosState, RootState>, receiptProps: any) {
    posApi.addClientToOrder(receiptProps);

    return dispatch('socketAddClientToOrder', receiptProps, { root: true });
  },
  async removeClientFromOrder({ dispatch }: ActionContext<PosState, RootState>, order: TakeAway) {
    posApi.removeClientFromOrder(order.id);

    return dispatch('socketRemoveClientFromOrder', order, { root: true });
  },
  async setNewClientToOrder({ dispatch }: ActionContext<PosState, RootState>, receiptProps: any) {
    receiptProps.client = await posApi.addClientToOrder(receiptProps);

    return dispatch('socketAddClientToOrder', receiptProps, { root: true });
  },
  getFetchPosStateRecursiveByTime({ commit }: ActionContext<PosState, RootState>, { timeInMilliseconds, posId }: { timeInMilliseconds: number, posId: string | null }) {
    return asyncRecursive(async () => {
      return posApi.getPosState(posId)
        .then((posState: any) => {
          commit('setPosState', new Pos(posState));
        })
        .catch(() => {
          commit('setPosState', new Pos());
        });
    }, timeInMilliseconds);
  },
  async addProductToOrder(
    { commit, state }: ActionContext<PosState, RootState>,
    payload: { product: Product, ingredients: GroupIngredientsProps, gtin: string },
  ) {
    const orderProduct = PosOrderProduct.createByProductAndIngredients(payload);
    const productInOrder = state.order.products.find(
      ({ hash }: OrderProductInterface) => hash === orderProduct.hash,
    );

    if (productInOrder !== undefined) {
      commit('incrementProductCountByProductHash', { productHash: orderProduct.hash, gtin: payload.gtin });
    } else {
      commit('addProductToOrder', orderProduct);
    }
  },
  async removeProductFromOrderByProductHash({ commit, state }: ActionContext<PosState, RootState>, { productHash, gtin }: { productHash: string, gtin: string }) {
    const productInOrder = state.order.products.find(({ hash }: OrderProductInterface) => hash === productHash);

    if (productInOrder !== undefined && productInOrder.count > 1) {
      commit('decrementProductCountByProductHash', { productHash, gtin });
    } else {
      commit('removeProductFromOrderByProductHash', productHash);
    }
  },
  async refundOrder({ dispatch }: ActionContext<PosState, RootState>, payload: { id: string, posId: string | null, products: Record<string, { count: number, gtins: string[] }> }) {
    await posApi.refundOrder(payload);

    return dispatch('socketRefundOrder', payload, { root: true });
  },
  async refundProducts({ dispatch }: ActionContext<PosState, RootState>, payload: { id: string, posId: string | null, products: Record<string, { count: number, gtins: string[] }> }) {
    await posApi.refundProducts(payload);

    return dispatch('socketRefundProducts', payload, { root: true });
  },
  async removeReceipt({ dispatch }: ActionContext<PosState, RootState>, payload: any) {
    orderApi.removeOrder(payload.id);

    return dispatch('socketRemoveTakeAwayOrder', payload, { root: true });
  },
  async createClient({ dispatch }: ActionContext<PosState, RootState>, clientProps: any) {
    const client: any = await posApi.createClient(clientProps);

    dispatch('socketCreateClient', client, { root: true });

    return client;
  },
};

const mutations: MutationTree<PosState> = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setSectionNotification(state, sectionNotification: any) {
    state.sectionNotification = sectionNotification;
  },
  setActualOrders(state, orders) {
    state.actualOrders = orders;
  },
  setPosState(state, posState) {
    state.posState = posState;
  },
  setSavedReceipts(state, savedReceipts: TakeAway[]) {
    state.savedReceipts = savedReceipts;
  },
  setReceiptsByDay(state, receipts: TakeAway[]) {
    state.receipts = receipts;
  },
  addReceipts(state, receipts: TakeAway[]) {
    state.receipts.push(...receipts);
  },
  updateNotPrintedReceipt(state, receiptProps: TakeAwayProps) {
    const receipt = state.receipts.find((receipt: TakeAway) => receipt.id === receiptProps.id);

    if (receipt !== undefined) {
      receipt.isPrinted = true;
      receipt.typeOfPayment = receiptProps.typeOfPayment;
    }
  },
  removeSavedReceipt(state, receiptId: string) {
    if (state.isReceiptsWasFetch) {
      const receiptIndex = state.savedReceipts.findIndex(({ id }: TakeAway) => id === receiptId);

      if (receiptIndex !== -1) {
        state.savedReceipts.splice(receiptIndex, 1);

        if (state.sectionNotification.savedOrdersCount < 1) {
          state.sectionNotification.savedOrdersCount = 0;

          return;
        }

        state.sectionNotification.savedOrdersCount--;
      }
    }
  },
  removeReceipt(state, receiptId: string) {
    const receiptIndex = state.receipts.findIndex(({ id }: TakeAway) => id === receiptId);

    if (receiptIndex !== -1) {
      state.receipts.splice(receiptIndex, 1);
    }
  },
  takeOrder(state, { id: orderId, staff }: any) {
    const order: TakeAway | undefined = state.receipts.find(({ id }: TakeAway) => id === orderId);

    if (order !== undefined) {
      order.take(new OrderStaff(staff));
    }
  },
  refuseOrder(state, orderId: string) {
    const order: TakeAway | undefined = state.receipts.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.refuse();
    }
  },
  doneOrder(state, orderId: string) {
    const order: TakeAway | undefined = state.receipts.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.done();
    }
  },
  undoneOrder(state, orderId: string) {
    const order: TakeAway | undefined = state.receipts.find(({ id }) => id === orderId);

    if (order !== undefined) {
      order.undone();
    }
  },
  addClientToReceipt(state, receiptProps: any) {
    if (state.isReceiptsWasFetch) {
      const receipt = state.receipts.find(({ id }: TakeAway) => id === receiptProps.id);

      if (receipt !== undefined) {
        receipt.client = receiptProps.client;
      }
    }
  },
  addClientToSavedReceipt(state, receiptProps: any) {
    if (state.isReceiptsWasFetch) {
      const savedReceipt = state.savedReceipts.find(({ id }: TakeAway) => id === receiptProps.id);

      if (savedReceipt !== undefined) {
        savedReceipt.client = receiptProps.client;
      }
    }
  },
  addClientToActualOrder(state, orderProps: any) {
    if (state.isActualOrdersWasFetch) {
      const actualOrder = state.actualOrders.find(({ id }: TakeAway) => id === orderProps.id);

      if (actualOrder !== undefined) {
        actualOrder.client = orderProps.client;
      }
    }
  },
  removeClientFromReceipt(state, receiptProps: any) {
    if (state.isReceiptsWasFetch) {
      const receipt = state.receipts.find(({ id }: TakeAway) => id === receiptProps.id);

      if (receipt !== undefined) {
        receipt.removeClient();
      }
    }
  },
  removeClientFromSavedReceipt(state, receiptProps: any) {
    if (state.isReceiptsWasFetch) {
      const savedReceipt = state.savedReceipts.find(({ id }: TakeAway) => id === receiptProps.id);

      if (savedReceipt !== undefined) {
        savedReceipt.removeClient();
      }
    }
  },
  removeClientFromActualOrder(state, orderProps: any) {
    if (state.isActualOrdersWasFetch) {
      const actualOrder = state.actualOrders.find(({ id }: TakeAway) => id === orderProps.id);

      if (actualOrder !== undefined) {
        actualOrder.removeClient();
      }
    }
  },
  refundProducts(state, receiptProps: any) {
    if (state.isReceiptsWasFetch) {
      const receipt = state.receipts.find(({ id }: TakeAway) => id === receiptProps.id);

      // Todo: add refund method to TakeAway model
      if (receipt !== undefined) {
        for (let i = 0; i < receipt.products.length; i++) {
          const refundProduct = receiptProps.products[receipt.products[i].id];

          if (refundProduct === undefined) {
            continue;
          }

          const refundCount = refundProduct.count;
          const refundGtins = refundProduct.gtins;

          receipt.products[i].refundCount += refundCount;
          receipt.products[i].count -= refundCount;
          receipt.products[i].gtins = receipt.products[i].gtins.filter((gtin: string) => !refundGtins.includes(gtin));

          const cost = receipt.products[i].cost * refundCount;
          const discountAmount = receipt.products[i].fullDiscountAmount * refundCount;

          receipt.totalSpent = numberUtils.correctFloatNumber(receipt.totalSpent - cost);
          receipt.discountAmount = numberUtils.correctFloatNumber(receipt.discountAmount - discountAmount);
        }
      }
    }
  },
  refundOrder(state, receiptId: string) {
    const receipt = state.receipts.find(({ id }: TakeAway) => id === receiptId);

    if (receipt !== undefined) {
      receipt.isRefund = true;

      for (let i = 0; i < receipt.products.length; i++) {
        receipt.products[i].refundCount += receipt.products[i].count;
        receipt.products[i].count = 0;
        receipt.products[i].gtins = [];
      }
    }
  },
  createReceipt(state, receiptProps: TakeAwayProps) {
    if (state.isReceiptsWasFetch) {
      const receipt = TakeAway.createFromRequest(receiptProps);

      state.receipts.unshift(receipt);
    }
  },
  createSavedReceipt(state, savedReceipt: TakeAwayProps) {
    if (state.isReceiptsWasFetch) {
      state.savedReceipts.unshift(TakeAway.createFromRequest(savedReceipt));
    }
  },
  addActualOrder(state, takeAwayProps: TakeAwayProps) {
    if (state.isActualOrdersWasFetch) {
      state.actualOrders.push(TakeAway.createFromRequest(takeAwayProps));
    }
  },
  takeActualOrder(state, { id: orderId, staff }: any) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }: TakeAway) => id === orderId);

    if (order !== undefined) {
      order.take(new OrderStaff(staff));
    }
  },
  refuseActualOrder(state, orderId: string) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }: TakeAway) => id === orderId);

    if (order !== undefined) {
      order.refuse();
    }
  },
  doneActualOrder(state, orderId: string) {
    if (state.isActualOrdersWasFetch) {
      const order: TakeAway | undefined = state.actualOrders.find(({ id }: TakeAway) => id === orderId);

      if (order !== undefined) {
        order.done();
      }
    }
  },
  undoneActualOrder(state, orderId: string) {
    const order: TakeAway | undefined = state.actualOrders.find(({ id }: TakeAway) => id === orderId);

    if (order !== undefined) {
      order.undone();
    }
  },
  removeActualOrder(state, orderId: string) {
    if (state.isActualOrdersWasFetch) {
      const orderIndex = state.actualOrders.findIndex(({ id }) => id === orderId);

      if (orderIndex !== -1) {
        state.actualOrders.splice(orderIndex, 1);
      }
    }
  },
  selectDiscount(state, discount: number | null) {
    state.order.discount = discount;
  },
  removeOrder(state) {
    state.order.products = [];
    state.order.client = null;
    state.order.paymentMethod = null;
    state.order.comment = '';
    state.order.discount = null;
    state.cash = '';
    state.card = '';
  },
  addProductToOrder(state, orderProduct: OrderProductInterface) {
    state.order.products.push(orderProduct);
  },
  removeProductFromOrderByProductHash(state, productHash: string) {
    const productIndex = state.order.products.findIndex(({ hash }: OrderProductInterface) => hash === productHash);

    state.order.products.splice(productIndex, 1);
  },
  incrementProductCountByProductHash(state, { productHash, gtin }: { productHash: string, gtin: string }) {
    const productInOrder = state.order.products.find(({ hash }: OrderProductInterface) => hash === productHash);

    if (productInOrder !== undefined) {
      productInOrder.increment(gtin);
    }
  },
  decrementProductCountByProductHash(state, { productHash, gtin }: { productHash: string, gtin: string }) {
    const productInOrder = state.order.products.find(({ hash }: OrderProductInterface) => hash === productHash);

    if (productInOrder !== undefined) {
      productInOrder.decrement(gtin);
    }
  },
  recalculateSavedReceiptsCount(state) {
    state.sectionNotification.savedOrdersCount = state.savedReceipts.length;
  },
  setClient(state, client) {
    state.order.client = client;
  },
  removeClient(state) {
    state.order.client = null;
  },
  addComment(state, comment: string) {
    state.order.comment = comment;
  },
  setPaymentMethod(state, paymentMethod) {
    state.order.paymentMethod = paymentMethod;
  },
  setIsActualOrdersFetching(state, isActualOrdersFetching: boolean) {
    state.isActualOrdersFetching = isActualOrdersFetching;
  },
  setIsActualOrdersWasFetch(state, isActualOrdersWasFetch: boolean) {
    state.isActualOrdersWasFetch = isActualOrdersWasFetch;
  },
  setIsReceiptsFetching(state, isReceiptsFetching: boolean) {
    state.isReceiptsFetching = isReceiptsFetching;
  },
  setIsReceiptsWasFetch(state, isReceiptsWasFetch: boolean) {
    state.isReceiptsWasFetch = isReceiptsWasFetch;
  },
  setAllReceiptsWasFetched(state, value: boolean) {
    state.allReceiptsWasFetched = value;
  },
  incrementSavedReceiptsCount(state) {
    state.sectionNotification.savedOrdersCount++;
  },
  incrementActualOrdersCount(state) {
    state.sectionNotification.actualOrdersCount++;
  },
  decrementActualOrdersCount(state) {
    if (state.sectionNotification.actualOrdersCount < 1) {
      state.sectionNotification.actualOrdersCount = 0;

      return;
    }

    state.sectionNotification.actualOrdersCount--;
  },
  setIsForceOff(state, isForceOff: boolean) {
    state.isForceOff = isForceOff;
  },
  selectCategory(state, category: any) {
    state.selectedCategory = category;
  },
  setCash(state, cash: number | string) {
    state.cash = cash;
  },
  setCard(state, card: number | string) {
    state.card = card;
  },
  bumpSyncRequestId(state) {
    state.syncRequestId += 1;
  },
};

export default {
  namespaced,
  modules,
  state,
  getters,
  actions,
  mutations,
};
